/**
 * Centralized logger utility for React/TypeScript
 * Provides consistent logging across the application with different log levels
 */

enum LogLevel {
    ERROR = "ERROR",
    WARN = "WARN",
    INFO = "INFO",
    DEBUG = "DEBUG",
}

/**
 * Logger class with different log levels
 * @class Logger
 * @constructor
 * @param context - Context identifier for the logger (e.g., "Auth", "Frontend")
 * @property {string} context - Context identifier for the logger
 * @property {boolean} isDevelopment - Flag indicating whether the environment is development
 * @method error - Logs an error message with optional context
 * @method warn - Logs a warning message with optional context
 * @method info - Logs an informational message with optional context
 * @method log - Logs a generic message with optional context
 * @method debug - Logs a debug message with optional context
 */
class Logger {
    private readonly context: string;
    private readonly isDevelopment: boolean;

    constructor(context: string = "") {
        this.context = context;
        this.isDevelopment = process.env.NODE_ENV !== "production";
    }

    private formatMessage(level: LogLevel, message: string): string {
        const timestamp = new Date().toISOString();
        const contextStr = this.context ? `[${this.context}]` : "";
        return `${timestamp} ${level} ${contextStr} ${message}`;
    }

    error(message: string, ...args: unknown[]): void {
        console.error(this.formatMessage(LogLevel.ERROR, message), ...args);
    }

    warn(message: string, ...args: unknown[]): void {
        console.warn(this.formatMessage(LogLevel.WARN, message), ...args);
    }

    info(message: string, ...args: unknown[]): void {
        // eslint-disable-next-line no-console
        console.log(this.formatMessage(LogLevel.INFO, message), ...args);
    }

    log(message: string, ...args: unknown[]): void {
        // eslint-disable-next-line no-console
        console.log(this.formatMessage(LogLevel.INFO, message), ...args);
    }

    debug(message: string, ...args: unknown[]): void {
        if (this.isDevelopment) {
            // eslint-disable-next-line no-console
            console.log(this.formatMessage(LogLevel.DEBUG, message), ...args);
        }
    }
}

/**
 * Create a logger instance with optional context
 * @param context - Context identifier for the logger (e.g., "Auth", "Frontend")
 * @returns {Logger} - Logger instance with the specified context
 */
export function createLogger(context: string = ""): Logger {
    return new Logger(context);
}

export const logger = new Logger();
